import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Trash2, Pencil, Users } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Field, SelectField, inputCls } from "../../components/forms/Field";
import { api, errMsg, fileUrl, formatDate } from "../../lib/api";
import { GALLERY_CATEGORIES, NEWS_CATEGORIES } from "../../data/content";
import { Td, Th } from "./shared";

const uploadFile = async (file) => {
  const fd = new FormData(); fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd);
  return data.file_id;
};

const Card = ({ title, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-6 lg:col-span-2"><h3 className="mb-5 font-display font-bold text-navy">{title}</h3>{children}</div>
);

const useList = (key, url) => {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: [key], queryFn: () => api.get(url).then((r) => r.data) });
  return { ...q, data: q.data || [], refresh: () => { qc.invalidateQueries(); } };
};

const NEWS_EMPTY = { title: "", category: "", summary: "", body: "", image_file_id: null, published: true };

export const NewsTab = () => {
  const { data, refresh } = useList("admin-news", "/admin/news");
  const [f, setF] = useState(NEWS_EMPTY);
  const [editId, setEditId] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.category) return toast.error("Choose a category");
    setBusy(true);
    try {
      const payload = { ...f, image_file_id: file ? await uploadFile(file) : f.image_file_id };
      editId ? await api.put(`/admin/news/${editId}`, payload) : await api.post("/admin/news", payload);
      toast.success(editId ? "Article updated" : "Article published");
      setF(NEWS_EMPTY); setFile(null); setEditId(null); refresh();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const edit = (n) => { setEditId(n.id); setF({ title: n.title, category: n.category, summary: n.summary, body: n.body, image_file_id: n.image_file_id, published: n.published }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (id) => { if (!window.confirm("Delete this article?")) return; await api.delete(`/admin/news/${id}`); refresh(); };
  return (
    <div className="grid gap-6 lg:grid-cols-5">
      <Card title={editId ? "Edit article" : "Add news / announcement"}>
        <form onSubmit={submit} className="space-y-4" data-testid="news-admin-form">
          <Field label="Title" required><Input className={inputCls} required value={f.title} onChange={set("title")} data-testid="news-title-input" /></Field>
          <Field label="Category" required><SelectField value={f.category} onChange={set("category")} options={NEWS_CATEGORIES} testId="news-category-select" /></Field>
          <Field label="Summary" required><Textarea rows={2} required value={f.summary} onChange={set("summary")} data-testid="news-summary-input" /></Field>
          <Field label="Body" required><Textarea rows={6} required value={f.body} onChange={set("body")} data-testid="news-body-input" /></Field>
          <Field label="Cover image (optional)"><Input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="news-image-input" /></Field>
          <label className="flex items-center gap-3 text-sm"><Switch checked={f.published} onCheckedChange={set("published")} data-testid="news-published-switch" /> Published</label>
          <div className="flex gap-2">
            <button type="submit" disabled={busy} className="btn-primary flex-1" data-testid="news-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{editId ? "Update" : "Publish"}</button>
            {editId && <button type="button" onClick={() => { setEditId(null); setF(NEWS_EMPTY); }} className="btn-outline">Cancel</button>}
          </div>
        </form>
      </Card>
      <div className="space-y-3 lg:col-span-3">
        {data.map((n, i) => (
          <div key={n.id} className="flex items-start gap-4 rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-news-${i}`}>
            <div className="min-w-0 flex-1"><p className="font-semibold text-navy">{n.title}</p><p className="text-xs text-slate-500">{n.category} · {formatDate(n.published_at)} {!n.published && "· Draft"}</p></div>
            <button onClick={() => edit(n)} className="rounded p-2 hover:bg-slate-100" data-testid={`admin-news-edit-${i}`}><Pencil className="h-4 w-4 text-royal" /></button>
            <button onClick={() => del(n.id)} className="rounded p-2 hover:bg-red-50" data-testid={`admin-news-delete-${i}`}><Trash2 className="h-4 w-4 text-red-600" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const EVENT_EMPTY = { title: "", date: "", time: "", venue: "", description: "", registration_open: true, published: true };

const RegistrationsDialog = ({ event, onClose }) => {
  const { data = [] } = useQuery({ queryKey: ["regs", event?.id], enabled: !!event, queryFn: () => api.get(`/admin/events/${event.id}/registrations`).then((r) => r.data) });
  return (
    <Dialog open={!!event} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="registrations-dialog">
        <DialogHeader><DialogTitle className="text-navy">Registrations – {event?.title}</DialogTitle></DialogHeader>
        <div className="max-h-[60vh] overflow-auto">
          <table className="w-full"><thead><tr><Th>Name</Th><Th>Mobile</Th><Th>Email</Th><Th>Employer</Th></tr></thead>
            <tbody className="divide-y">{data.map((r) => <tr key={r.id}><Td>{r.name}</Td><Td>{r.mobile}</Td><Td>{r.email}</Td><Td>{r.employer}</Td></tr>)}</tbody></table>
          {!data.length && <p className="p-6 text-center text-sm text-slate-500">No registrations yet.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const EventsTab = () => {
  const { data, refresh } = useList("admin-events", "/admin/events");
  const [f, setF] = useState(EVENT_EMPTY);
  const [regs, setRegs] = useState(null);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/events", f); toast.success("Event created"); setF(EVENT_EMPTY); refresh(); } catch (err) { toast.error(errMsg(err)); }
  };
  const toggle = async (ev) => { const { id, registrations, created_at, ...rest } = ev; await api.put(`/admin/events/${id}`, { ...rest, registration_open: !ev.registration_open }); refresh(); };
  const del = async (id) => { if (!window.confirm("Delete this event?")) return; await api.delete(`/admin/events/${id}`); refresh(); };
  return (
    <div className="grid gap-6 lg:grid-cols-5">
      <Card title="Create event">
        <form onSubmit={submit} className="space-y-4" data-testid="event-admin-form">
          <Field label="Title" required><Input className={inputCls} required value={f.title} onChange={set("title")} data-testid="event-title-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date" required><Input className={inputCls} type="date" required value={f.date} onChange={set("date")} data-testid="event-date-input" /></Field>
            <Field label="Time"><Input className={inputCls} placeholder="10:00 AM" value={f.time} onChange={set("time")} data-testid="event-time-input" /></Field>
          </div>
          <Field label="Venue" required><Input className={inputCls} required value={f.venue} onChange={set("venue")} data-testid="event-venue-input" /></Field>
          <Field label="Description"><Textarea rows={3} value={f.description} onChange={set("description")} data-testid="event-description-input" /></Field>
          <button type="submit" className="btn-primary w-full" data-testid="event-save-button">Create event</button>
        </form>
      </Card>
      <div className="space-y-3 lg:col-span-3">
        {data.map((e, i) => (
          <div key={e.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-event-${i}`}>
            <div className="min-w-0 flex-1"><p className="font-semibold text-navy">{e.title}</p><p className="text-xs text-slate-500">{formatDate(e.date)} · {e.venue}</p></div>
            <button onClick={() => setRegs(e)} className="flex items-center gap-1 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold" data-testid={`admin-event-regs-${i}`}><Users className="h-3.5 w-3.5" />{e.registrations}</button>
            <label className="flex items-center gap-2 text-xs"><Switch checked={e.registration_open} onCheckedChange={() => toggle(e)} data-testid={`admin-event-toggle-${i}`} />Open</label>
            <button onClick={() => del(e.id)} className="rounded p-2 hover:bg-red-50" data-testid={`admin-event-delete-${i}`}><Trash2 className="h-4 w-4 text-red-600" /></button>
          </div>
        ))}
      </div>
      <RegistrationsDialog event={regs} onClose={() => setRegs(null)} />
    </div>
  );
};

export const GalleryTab = () => {
  const { data, refresh } = useList("admin-gallery", "/gallery");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file || !category) return toast.error("Choose an image and a category");
    setBusy(true);
    try {
      const image_file_id = await uploadFile(file);
      await api.post("/admin/gallery", { title, category, image_file_id });
      toast.success("Photo added"); setTitle(""); setFile(null); e.target.reset(); refresh();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const del = async (id) => { if (!window.confirm("Remove this photo?")) return; await api.delete(`/admin/gallery/${id}`); refresh(); };
  return (
    <div className="grid gap-6 lg:grid-cols-5">
      <Card title="Upload photo">
        <form onSubmit={submit} className="space-y-4" data-testid="gallery-admin-form">
          <Field label="Caption" required><Input className={inputCls} required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="gallery-title-input" /></Field>
          <Field label="Category" required><SelectField value={category} onChange={setCategory} options={GALLERY_CATEGORIES} testId="gallery-category-select" /></Field>
          <Field label="Image" required><Input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="gallery-image-input" /></Field>
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="gallery-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Upload</button>
        </form>
      </Card>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:col-span-3">
        {data.map((g, i) => (
          <div key={g.id} className="group relative overflow-hidden rounded-lg" data-testid={`admin-gallery-${i}`}>
            <img src={fileUrl(g.image_file_id)} alt={g.title} className="aspect-square w-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 bg-navy/80 p-2 text-xs text-white">{g.title}</div>
            <button onClick={() => del(g.id)} className="absolute right-2 top-2 rounded bg-white p-1.5" data-testid={`admin-gallery-delete-${i}`}><Trash2 className="h-4 w-4 text-red-600" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const DocumentsTab = () => {
  const { data, refresh } = useList("admin-docs", "/documents");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file");
    setBusy(true);
    try {
      const file_id = await uploadFile(file);
      await api.post("/admin/documents", { title, description, file_id });
      toast.success("Document published"); setTitle(""); setDescription(""); setFile(null); e.target.reset(); refresh();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const del = async (id) => { if (!window.confirm("Remove this document?")) return; await api.delete(`/admin/documents/${id}`); refresh(); };
  return (
    <div className="grid gap-6 lg:grid-cols-5">
      <Card title="Publish document">
        <form onSubmit={submit} className="space-y-4" data-testid="document-admin-form">
          <Field label="Title" required><Input className={inputCls} required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="document-title-input" /></Field>
          <Field label="Description"><Input className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="document-description-input" /></Field>
          <Field label="File (PDF or image, max 5 MB)" required><Input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="document-file-input" /></Field>
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="document-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Publish</button>
        </form>
      </Card>
      <div className="space-y-3 lg:col-span-3">
        {data.map((d, i) => (
          <div key={d.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-document-${i}`}>
            <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" className="min-w-0 flex-1"><p className="font-semibold text-navy">{d.title}</p><p className="text-xs text-slate-500">{d.filename}</p></a>
            <button onClick={() => del(d.id)} className="rounded p-2 hover:bg-red-50" data-testid={`admin-document-delete-${i}`}><Trash2 className="h-4 w-4 text-red-600" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};
